// frontend/src/PermissionsContext.js
// Provides a global permissions context fetched from backend /api/user/permissions.
// Usage: wrap root with <PermissionsProvider>. Then call usePermissions() in components.

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const PermissionsContext = createContext({
  permissions: new Set(),
  loading: false,
  error: null,
  refreshPermissions: async () => {},
  hasPermission: () => false,
  hasAllPermissions: () => false,
  hasAnyPermission: () => false,
  clearPermissions: () => {}
});

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const BASE_URL = process.env.REACT_APP_SERVER_URL || '';

  const normalize = useCallback((raw) => {
    if (!raw) return new Set();
    if (Array.isArray(raw)) return new Set(raw.filter(p => typeof p === 'string'));
    if (typeof raw === 'object') return new Set(Object.values(raw).filter(p => typeof p === 'string'));
    return new Set();
  }, []);

  const refreshPermissions = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/api/user/permissions`, { withCredentials: true });
      setPermissions(normalize(resp.data));
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
      setError(e.response?.data?.error || e.message || 'Failed to load permissions');
      setPermissions(new Set());
    } finally { setLoading(false); }
  }, [BASE_URL, normalize]);

  const clearPermissions = useCallback(() => { setPermissions(new Set()); setError(null); }, []);

  const hasPermission = useCallback((perm) => permissions.has('*') || (perm && permissions.has(perm)), [permissions]);
  const hasAllPermissions = useCallback((perms = []) => {
    if (!Array.isArray(perms) || perms.length === 0) return true;
    if (permissions.has('*')) return true;
    return perms.every(p => permissions.has(p));
  }, [permissions]);
  const hasAnyPermission = useCallback((perms = []) => {
    if (!Array.isArray(perms) || perms.length === 0) return true;
    if (permissions.has('*')) return true;
    return perms.some(p => permissions.has(p));
  }, [permissions]);

  const value = useMemo(() => ({
    permissions, loading, error,
    refreshPermissions, clearPermissions,
    hasPermission, hasAllPermissions, hasAnyPermission
  }), [permissions, loading, error, refreshPermissions, clearPermissions, hasPermission, hasAllPermissions, hasAnyPermission]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => useContext(PermissionsContext);
